import React, { useEffect, useState, useCallback } from 'react';
import { Users, Shield, Layers, Plus, Pencil, Trash2, X, Check, ShieldCheck } from 'lucide-react';
import { toastMessage } from '@/utils/toast-message';
import {
  rbacApi, type Role, type Staff, type ClassGroup, type PermissionCatalog,
} from './api';

// toastMessage is a (message, type) function — small adapters for readability.
const notifyOk = (m: string) => toastMessage(m, 'success');
const notifyErr = (m: string) => toastMessage(m, 'error');

type Tab = 'staff' | 'roles' | 'groups';
const SCOPE_LABEL: Record<string, string> = {
  school: 'Whole school', group: 'Class group(s)', section: 'Own sections', none: 'No access',
};

const UsersRoles: React.FC = () => {
  const [tab, setTab] = useState<Tab>('staff');

  return (
    <>
      <div className="page-head">
        <div className="page-head-icon"><ShieldCheck size={22} /></div>
        <div className="page-head-text">
          <h1>Users &amp; Roles</h1>
          <p>Staff access, roles &amp; permissions, and coordinator class groups</p>
        </div>
      </div>

      <div className="page-body">
        <div className="tabs" style={{ marginBottom: 24 }}>
          {([['staff', 'Staff', Users], ['roles', 'Roles & Permissions', Shield], ['groups', 'Class Groups', Layers]] as const).map(
            ([key, label, Icon]) => (
              <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>
                <span className="row gap-6" style={{ alignItems: 'center' }}><Icon size={15} /> {label}</span>
              </button>
            ),
          )}
        </div>

        {tab === 'staff' && <StaffTab />}
        {tab === 'roles' && <RolesTab />}
        {tab === 'groups' && <GroupsTab />}
      </div>
    </>
  );
};

/* ─────────────────────────── Staff tab ─────────────────────────── */
const StaffTab: React.FC = () => {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [groups, setGroups] = useState<ClassGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, r, g] = await Promise.all([rbacApi.getStaff(), rbacApi.getRoles(), rbacApi.getClassGroups()]);
    setStaff(s.data?.data ?? []);
    setRoles(r.data?.data ?? []);
    setGroups(g.data?.data ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> Add user</button>
      </div>
      {!staff.length ? (
        <Empty icon={<Users size={32} />} title="No staff yet" text="Add staff members to assign them roles and access scopes." />
      ) : (
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="ur-table">
          <thead>
            <tr><th>Name</th><th>Role</th><th>Access scope</th><th>Class groups</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {staff.map((u) => (
              <tr key={u.id}>
                <td>
                  <div style={{ fontWeight: 700, color: 'var(--text)' }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{u.phone || u.email || '—'}</div>
                </td>
                <td>{u.staffRole?.name ?? <span style={{ color: 'var(--text-3)' }}>No role</span>}</td>
                <td style={{ color: 'var(--text-2)' }}>{SCOPE_LABEL[u.scope_type]}</td>
                <td style={{ color: 'var(--text-2)' }}>
                  {u.scope_type === 'group'
                    ? (u.classGroups.length ? u.classGroups.map((g) => g.name).join(', ') : <span style={{ color: 'var(--warning)' }}>None assigned</span>)
                    : <span style={{ color: 'var(--text-3)' }}>—</span>}
                </td>
                <td>
                  <span className="chip chip-sm" style={u.is_active
                    ? { background: 'var(--success-wash)', color: 'var(--success)' }
                    : { background: 'var(--slate-soft)', color: 'var(--text-3)' }}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-soft btn-sm" onClick={() => setEditing(u)}><Pencil size={14} /> Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {editing && (
        <StaffEditModal staff={editing} roles={roles} groups={groups} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {creating && (
        <StaffCreateModal roles={roles} groups={groups} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </>
  );
};

const StaffCreateModal: React.FC<{ roles: Role[]; groups: ClassGroup[]; onClose: () => void; onSaved: () => void; }> = ({ roles, groups, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [roleId, setRoleId] = useState<number | ''>('');
  const [scope, setScope] = useState<Staff['scope_type']>('none');
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { notifyErr('Name is required'); return; }
    setSaving(true);
    const res = await rbacApi.createStaff({
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      role_id: roleId === '' ? undefined : Number(roleId),
      scope_type: scope,
    });
    if (!res.success) { notifyErr(res.message || 'Failed to add user'); setSaving(false); return; }
    if (scope === 'group' && groupIds.length > 0) {
      await rbacApi.setStaffClassGroups((res.data as any).id, groupIds);
    }
    notifyOk('Staff member added');
    setSaving(false); onSaved();
  };

  return (
    <Modal title="Add user" onClose={onClose} onSave={save} saving={saving} saveLabel="Add user">
      <div className="field">
        <label>Full name <span className="req">*</span></label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Meena Krishnan" autoFocus />
      </div>
      <div className="field">
        <label>Email <span className="opt">(optional)</span></label>
        <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@school.edu" />
      </div>
      <div className="field">
        <label>Login phone <span className="opt">(for OTP sign-in)</span></label>
        <input className="input" value={phone} maxLength={10} placeholder="10-digit mobile number"
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} />
      </div>
      <div className="field">
        <label>Role</label>
        <select className="select" value={roleId} onChange={(e) => setRoleId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">No role</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label>Access scope</label>
        <select className="select" value={scope} onChange={(e) => setScope(e.target.value as Staff['scope_type'])}>
          <option value="school">Whole school</option>
          <option value="group">Class group(s) — coordinator</option>
          <option value="section">Own sections — class/subject teacher</option>
          <option value="none">No data access</option>
        </select>
      </div>
      {scope === 'group' && (
        <div className="field">
          <label>Assigned class groups</label>
          {groups.length ? (
            <div className="ur-checklist">
              {groups.map((g) => (
                <label key={g.id} className="ur-check">
                  <input type="checkbox" checked={groupIds.includes(g.id)}
                    onChange={(e) => setGroupIds((p) => e.target.checked ? [...p, g.id] : p.filter((x) => x !== g.id))} />
                  {g.name}
                </label>
              ))}
            </div>
          ) : <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: 0 }}>No class groups yet — create them in the Class Groups tab.</p>}
        </div>
      )}
    </Modal>
  );
};

const StaffEditModal: React.FC<{ staff: Staff; roles: Role[]; groups: ClassGroup[]; onClose: () => void; onSaved: () => void; }> = ({ staff, roles, groups, onClose, onSaved }) => {
  const [name, setName] = useState(staff.name ?? '');
  const [email, setEmail] = useState(staff.email ?? '');
  const [roleId, setRoleId] = useState<number | ''>(staff.role_id ?? '');
  const [scope, setScope] = useState(staff.scope_type);
  const [active, setActive] = useState(staff.is_active);
  const [groupIds, setGroupIds] = useState<number[]>(staff.classGroups.map((g) => g.id));
  const [phone, setPhone] = useState(staff.phone ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { notifyErr('Name is required'); return; }
    setSaving(true);
    const res = await rbacApi.updateStaff(staff.id, {
      name: name.trim(), email: email.trim(),
      role_id: roleId === '' ? undefined : Number(roleId),
      phone: phone.trim(), scope_type: scope, is_active: active,
    });
    if (!res.success) { notifyErr(res.message || 'Failed to update'); setSaving(false); return; }
    if (scope === 'group') await rbacApi.setStaffClassGroups(staff.id, groupIds);
    notifyOk('Staff updated');
    setSaving(false); onSaved();
  };

  return (
    <Modal title={`Edit — ${staff.name}`} onClose={onClose} onSave={save} saving={saving}>
      <div className="field">
        <label>Full name <span className="req">*</span></label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
      </div>
      <div className="field">
        <label>Email <span className="opt">(optional)</span></label>
        <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@school.edu" />
      </div>
      <div className="field">
        <label>Login phone <span className="opt">(for OTP sign-in)</span></label>
        <input className="input" value={phone} maxLength={10} placeholder="10-digit mobile number"
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} />
      </div>
      <div className="field">
        <label>Role</label>
        <select className="select" value={roleId} onChange={(e) => setRoleId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">No role</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label>Access scope</label>
        <select className="select" value={scope} onChange={(e) => setScope(e.target.value as Staff['scope_type'])}>
          <option value="school">Whole school</option>
          <option value="group">Class group(s) — coordinator</option>
          <option value="section">Own sections — class/subject teacher</option>
          <option value="none">No data access</option>
        </select>
      </div>
      {scope === 'group' && (
        <div className="field">
          <label>Assigned class groups</label>
          {groups.length ? (
            <div className="ur-checklist">
              {groups.map((g) => (
                <label key={g.id} className="ur-check">
                  <input type="checkbox" checked={groupIds.includes(g.id)}
                    onChange={(e) => setGroupIds((p) => e.target.checked ? [...p, g.id] : p.filter((x) => x !== g.id))} />
                  {g.name}
                </label>
              ))}
            </div>
          ) : <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: 0 }}>No class groups yet — create them in the Class Groups tab.</p>}
        </div>
      )}
      <label className="ur-check" style={{ marginTop: 4 }}>
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
      </label>
    </Modal>
  );
};

/* ─────────────────────────── Roles tab ─────────────────────────── */
const RolesTab: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, c] = await Promise.all([rbacApi.getRoles(), rbacApi.getPermissions()]);
    setRoles(r.data?.data ?? []);
    setCatalog(c.data ?? null);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (role: Role) => {
    if (!confirm(`Delete role "${role.name}"?`)) return;
    const res = await rbacApi.deleteRole(role.id);
    if (res.success) { notifyOk('Role deleted'); load(); } else notifyErr(res.message || 'Failed');
  };

  if (loading || !catalog) return <Spinner />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> New role</button>
      </div>
      <div className="ur-grid">
        {roles.map((r) => (
          <div key={r.id} className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="row gap-8" style={{ fontWeight: 800, color: 'var(--text)', fontSize: 15 }}>
                  {r.name}
                  {r.is_system_role && <span className="chip chip-sm" style={{ background: 'var(--slate-soft)', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>default</span>}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 3 }}>{r.description || '—'}</div>
              </div>
              {!r.is_active && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>inactive</span>}
            </div>
            <div className="row gap-16" style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 14 }}>
              <span><b style={{ color: 'var(--text)' }}>{r.permissions.length}</b> permissions</span>
              <span><b style={{ color: 'var(--text)' }}>{r.user_count}</b> staff</span>
            </div>
            <div className="row gap-8" style={{ marginTop: 16 }}>
              <button className="btn btn-soft btn-sm" style={{ flex: 1 }} onClick={() => setEditing(r)}><Pencil size={14} /> Permissions</button>
              {!r.is_system_role && <button className="btn btn-danger btn-sm" onClick={() => del(r)}><Trash2 size={14} /></button>}
            </div>
          </div>
        ))}
      </div>
      {editing && <RoleMatrixModal role={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <RoleCreateModal catalog={catalog} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </>
  );
};

const MatrixEditor: React.FC<{ catalog: PermissionCatalog; selected: Set<number>; toggle: (id: number) => void; }> = ({ catalog, selected, toggle }) => (
  <div className="ur-matrix">
    {Object.entries(catalog.byModule).map(([mod, actions]) => (
      <div key={mod} className="ur-matrix-row">
        <div className="ur-matrix-mod">{mod.replace(/_/g, ' ')}</div>
        <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
          {actions.map((a) => (
            <button key={a.id} type="button" onClick={() => toggle(a.id)}
              className={`ur-perm${selected.has(a.id) ? ' on' : ''}`}>
              {selected.has(a.id) && <Check size={12} />}{a.action}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const RoleMatrixModal: React.FC<{ role: Role; catalog: PermissionCatalog; onClose: () => void; onSaved: () => void; }> = ({ role, catalog, onClose, onSaved }) => {
  const [selected, setSelected] = useState<Set<number>>(new Set(role.permission_ids));
  const [saving, setSaving] = useState(false);
  const toggle = (id: number) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    setSaving(true);
    const res = await rbacApi.setRolePermissions(role.id, [...selected]);
    if (res.success) { notifyOk('Permissions updated'); onSaved(); } else { notifyErr(res.message || 'Failed'); setSaving(false); }
  };
  return (
    <Modal title={`Permissions — ${role.name}`} wide onClose={onClose} onSave={save} saving={saving}>
      <MatrixEditor catalog={catalog} selected={selected} toggle={toggle} />
    </Modal>
  );
};

const RoleCreateModal: React.FC<{ catalog: PermissionCatalog; onClose: () => void; onSaved: () => void; }> = ({ catalog, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const toggle = (id: number) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    if (!name.trim()) { notifyErr('Role name is required'); return; }
    setSaving(true);
    const res = await rbacApi.createRole({ name, description, permission_ids: [...selected] });
    if (res.success) { notifyOk('Role created'); onSaved(); } else { notifyErr(res.message || 'Failed'); setSaving(false); }
  };
  return (
    <Modal title="New custom role" wide onClose={onClose} onSave={save} saving={saving} saveLabel="Create role">
      <div className="ur-form-2" style={{ marginBottom: 14 }}>
        <div className="field"><label>Role name <span className="req">*</span></label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sports Head" /></div>
        <div className="field"><label>Description</label><input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" /></div>
      </div>
      <MatrixEditor catalog={catalog} selected={selected} toggle={toggle} />
    </Modal>
  );
};

/* ─────────────────────────── Class Groups tab ─────────────────────────── */
const GroupsTab: React.FC = () => {
  const [groups, setGroups] = useState<ClassGroup[]>([]);
  const [classes, setClasses] = useState<{ id: number; class_label: string; curriculum_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ClassGroup | null>(null);
  const [creatingName, setCreatingName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [g, c] = await Promise.all([rbacApi.getClassGroups(), rbacApi.getCurriculumClasses()]);
    setGroups(g.data?.data ?? []);
    const flat = (c.data?.grouped ?? []).flatMap((grp) => grp.classes.map((cl) => ({ id: cl.id, class_label: cl.class_label, curriculum_name: grp.curriculum_name })));
    setClasses(flat);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!creatingName.trim()) return;
    const res = await rbacApi.createClassGroup({ name: creatingName });
    if (res.success) { setCreatingName(''); notifyOk('Group created'); load(); } else notifyErr(res.message || 'Failed');
  };
  const del = async (g: ClassGroup) => {
    if (!confirm(`Delete group "${g.name}"?`)) return;
    const res = await rbacApi.deleteClassGroup(g.id);
    if (res.success) { notifyOk('Group deleted'); load(); } else notifyErr(res.message || 'Failed');
  };

  if (loading) return <Spinner />;

  return (
    <>
      <div className="row gap-8" style={{ marginBottom: 16 }}>
        <input className="input" style={{ maxWidth: 320 }} value={creatingName} placeholder="New group name e.g. Primary (1-5)"
          onChange={(e) => setCreatingName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} />
        <button className="btn btn-primary" onClick={create}><Plus size={16} /> Add group</button>
      </div>
      {!groups.length
        ? <Empty icon={<Layers size={32} />} title="No class groups yet" text="Create groups like “Primary (1-5)” to scope coordinators to a set of classes." />
        : (
          <div className="ur-grid">
            {groups.map((g) => (
              <div key={g.id} className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 800, color: 'var(--text)', fontSize: 15 }}>{g.name}</div>
                  <button className="btn btn-danger btn-sm" onClick={() => del(g)}><Trash2 size={14} /></button>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 8 }}>
                  {g.classes.length ? g.classes.map((c) => c.name).join(', ') : <span style={{ color: 'var(--warning)' }}>No classes assigned</span>}
                </div>
                <button className="btn btn-soft btn-sm" style={{ width: '100%', marginTop: 14 }} onClick={() => setEditing(g)}><Pencil size={14} /> Assign classes</button>
              </div>
            ))}
          </div>
        )}
      {editing && <GroupClassesModal group={editing} classes={classes} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
};

const GroupClassesModal: React.FC<{ group: ClassGroup; classes: { id: number; class_label: string; curriculum_name: string }[]; onClose: () => void; onSaved: () => void; }> = ({ group, classes, onClose, onSaved }) => {
  const [ids, setIds] = useState<number[]>(group.curriculum_class_ids);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const res = await rbacApi.setClassGroupMembers(group.id, ids);
    if (res.success) { notifyOk('Classes assigned'); onSaved(); } else { notifyErr(res.message || 'Failed'); setSaving(false); }
  };
  return (
    <Modal title={`Classes — ${group.name}`} onClose={onClose} onSave={save} saving={saving}>
      <p style={{ fontSize: 12.5, color: 'var(--text-3)', margin: '0 0 10px' }}>A class belongs to one group; assigning here moves it from any other group.</p>
      <div className="ur-checklist" style={{ maxHeight: '52vh' }}>
        {classes.map((c) => (
          <label key={c.id} className="ur-check">
            <input type="checkbox" checked={ids.includes(c.id)} onChange={(e) => setIds((p) => e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id))} />
            <span style={{ color: 'var(--text)' }}>{c.class_label}</span>
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{c.curriculum_name}</span>
          </label>
        ))}
        {!classes.length && <p style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No curriculum classes found.</p>}
      </div>
    </Modal>
  );
};

/* ─────────────────────────── shared bits ─────────────────────────── */
const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
    <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const Empty: React.FC<{ icon: React.ReactNode; title: string; text: string }> = ({ icon, title, text }) => (
  <div className="empty">
    <div className="empty-art">{icon}</div>
    <h3>{title}</h3>
    <p>{text}</p>
  </div>
);

const Modal: React.FC<{
  title: string; wide?: boolean; onClose: () => void; onSave: () => void;
  saving: boolean; saveLabel?: string; children: React.ReactNode;
}> = ({ title, wide, onClose, onSave, saving, saveLabel = 'Save', children }) => (
  <>
    <div className="scrim" onClick={onClose} />
    <div className="modal" style={{ maxWidth: wide ? 680 : 480 }}>
      <div className="modal-head">
        <div style={{ flex: 1 }}><h2>{title}</h2></div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
      </div>
      <div className="modal-body col gap-14">{children}</div>
      <div className="modal-foot">
        <button className="btn btn-soft" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : saveLabel}</button>
      </div>
    </div>
  </>
);

export default UsersRoles;
